import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Practice } from 'src/app/interfaces/practice';
import { PracticeInscription } from 'src/app/interfaces/practiceinscription';
import { AuthService } from 'src/app/services/auth.service';
import {
  PracticeInscriptionMutationResponse,
  PracticeInscriptionService
} from 'src/app/services/practiceinscription.service';
import { PracticeService } from 'src/app/services/practice.service';

@Component({
  selector: 'app-add-edit-practiceinscription',
  templateUrl: './add-edit-practiceinscription.component.html',
  styleUrls: ['./add-edit-practiceinscription.component.css']
})
export class AddEditPracticeinscriptionComponent implements OnInit {
  listPractices: Practice[] = [];
  enrolledPractices: number[] = [];
  loading = false;
  isLoading = false;
  selectedPracticeId: number | null = null;

  constructor(
    private _practiceService: PracticeService,
    private _practiceInscriptionService: PracticeInscriptionService,
    private toastr: ToastrService,
    private router: Router,
    public authService: AuthService
  ) {}

  ngOnInit(): void {
    if (!this.authService.idstudent) {
      this.toastr.error('No se encontro un estudiante valido para la sesion actual', 'Error');
      this.router.navigate(['/login']);
      return;
    }

    this.loadEnrolledPractices();
    this.getListPractices();
  }

  getListPractices() {
    const studentId = this.authService.idstudent;

    if (!studentId) {
      this.listPractices = [];
      return;
    }

    this.loading = true;
    this._practiceService.getAvailablePractices(studentId).subscribe({
      next: (data: Practice[]) => {
        this.listPractices = data;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error al obtener la lista de practicas:', error);
        this.toastr.error('Error al cargar las practicas', 'Error');
        this.loading = false;
      }
    });
  }

  loadEnrolledPractices() {
    const studentId = this.authService.idstudent;
    if (!studentId) {
      return;
    }

    this._practiceInscriptionService.getPracticeInscriptionsByStudentId(studentId).subscribe({
      next: (data: PracticeInscription[]) => {
        this.enrolledPractices = data
          .filter((inscription) => Number(inscription.state ?? 1) === 1)
          .map((inscription) => inscription.idpractice);
      },
      error: (error) => {
        console.error('Error al cargar las inscripciones del estudiante:', error);
      }
    });
  }

  isAlreadyEnrolled(practiceId: number): boolean {
    return this.enrolledPractices.includes(practiceId);
  }

  getAvailableSlots(practice: Practice): number {
    if (typeof practice.availableSlots === 'number') {
      return practice.availableSlots;
    }

    return Math.max(Number(practice.quota || 0) - Number(practice.enrolled || 0), 0);
  }

  private syncPracticeAvailability(practice?: Practice) {
    if (!practice?.id) {
      return;
    }

    this.listPractices = this.listPractices.map((item) => {
      if (item.id !== practice.id) {
        return item;
      }

      return {
        ...item,
        ...practice
      };
    });
  }

  private markStudentAsEnrolled(idpractice: number) {
    if (!this.enrolledPractices.includes(idpractice)) {
      this.enrolledPractices = [...this.enrolledPractices, idpractice];
    }
  }

  addPractice(idpractice: number) {
    const studentId = this.authService.idstudent;
    if (!studentId) {
      this.toastr.error('La sesion del estudiante no es valida', 'Error');
      return;
    }

    if (this.isAlreadyEnrolled(idpractice)) {
      this.toastr.warning('Ya estas inscrito en esta practica.', 'Advertencia');
      return;
    }

    const targetPractice = this.listPractices.find((practice) => practice.id === idpractice);
    if (targetPractice && this.getAvailableSlots(targetPractice) <= 0) {
      this.toastr.warning('Los cupos de esta practica ya fueron tomados. Actualiza la lista.', 'Sin cupos');
      return;
    }

    this.isLoading = true;
    this.selectedPracticeId = idpractice;

    const practiceinscription: PracticeInscription = {
      idstudent: studentId,
      idpractice,
      state: 1
    };

    this._practiceInscriptionService.savePracticeInscription(practiceinscription).subscribe({
      next: (response: PracticeInscriptionMutationResponse) => {
        this.syncPracticeAvailability(response.practice);
        this.markStudentAsEnrolled(idpractice);
        this.toastr.success(
          response.msg || 'Inscripcion de practica completada con exito',
          'Inscripcion registrada'
        );
        this.isLoading = false;
        this.selectedPracticeId = null;
        this.router.navigate(['/practiceinscription']);
      },
      error: (error) => {
        const payload = error?.error as PracticeInscriptionMutationResponse | undefined;
        this.syncPracticeAvailability(payload?.practice);
        console.error('Error al guardar la inscripcion de practica:', error);
        this.toastr.error(
          payload?.msg || 'La practica ya fue tomada o no hay suficientes cupos',
          'Error'
        );
        this.isLoading = false;
        this.selectedPracticeId = null;
      }
    });
  }
}
